<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  selectedIds: { type: Array, default: () => [] },
  newLabels: { type: Array, default: () => [] },
});
const emit = defineEmits(['update:selectedIds', 'update:newLabels']);

const store = useStore();
const labels = computed(() => store.getters['labels/getLabels'] || []);
const showCreate = ref(false);
const draftLabel = ref(emptyLabel());

function emptyLabel() {
  return { title: '', color: '#605AD2', description: '', show_on_sidebar: true };
}

onMounted(() => {
  if (!labels.value.length) store.dispatch('labels/get');
  document.addEventListener('keydown', onEsc);
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEsc);
});

function onEsc(e) {
  if (e.key === 'Escape' && showCreate.value) cancelCreate();
}

function isSelected(label) {
  return props.selectedIds.includes(label.id);
}
function toggle(label) {
  const next = isSelected(label)
    ? props.selectedIds.filter(id => id !== label.id)
    : [...props.selectedIds, label.id];
  emit('update:selectedIds', next);
}

function openCreate() {
  draftLabel.value = emptyLabel();
  showCreate.value = true;
}
function cancelCreate() {
  showCreate.value = false;
}
function saveDraft() {
  if (!draftLabel.value.title.trim()) return;
  emit('update:newLabels', [...props.newLabels, { ...draftLabel.value }]);
  showCreate.value = false;
}
function removeNewLabel(idx) {
  const arr = props.newLabels.slice();
  arr.splice(idx, 1);
  emit('update:newLabels', arr);
}
</script>

<template>
  <div class="sz-step-labels">
    <h4>Selecione ou crie as etiquetas que serão vinculadas</h4>

    <section>
      <h5>Etiquetas existentes</h5>
      <p class="sz-hint">Clique para adicionar e remover etiquetas</p>

      <!-- Wrapper com fade gradient inferior. Quando o form de criação está
           aberto, a lista colapsa (max-height menor) e o fade ajuda a
           sinalizar que há mais conteúdo escondido (igual ao Figma). -->
      <div
        class="sz-pill-list-wrap"
        :class="{ 'sz-pill-list-wrap--collapsed': showCreate }"
      >
        <div class="sz-pill-list">
          <button type="button" class="sz-pill sz-pill-create" @click="openCreate">
            <svg class="sz-pill-create-plus" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span class="sz-pill-create-text">Criar nova etiqueta</span>
          </button>
          <button
            v-for="lbl in labels"
            :key="lbl.id"
            type="button"
            class="sz-pill"
            :class="{ 'sz-pill-on': isSelected(lbl) }"
            :style="{ '--sz-label-color': lbl.color }"
            @click="toggle(lbl)"
          >
            <span class="sz-pill-dot" :style="{ background: lbl.color }" />
            <span class="sz-pill-text" :style="{ color: lbl.color }">{{ lbl.title }}</span>
            <span v-if="isSelected(lbl)" class="sz-pill-check">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            <svg v-else class="sz-pill-plus-svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>
        <div
          v-if="showCreate"
          class="sz-pill-list-fade"
          aria-hidden="true"
        ></div>
      </div>
    </section>

    <!-- Form inline (substitui o antigo modal) — abre logo abaixo da lista
         conforme Figma. Header com título + X, inputs em linha (Nome | Cor |
         Descrição), checkbox + botão Salvar no rodapé. -->
    <section v-if="showCreate" class="sz-create-inline">
      <header class="sz-create-inline-header">
        <h5>Criar nova etiqueta</h5>
        <button type="button" class="sz-close" aria-label="Fechar" @click="cancelCreate">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </header>

      <div class="sz-create-inline-body">
        <div class="sz-create-row">
          <label class="sz-create-field">
            <span class="sz-field-label">
              <span class="sz-required">*</span> Nome da etiqueta
            </span>
            <input
              v-model="draftLabel.title"
              type="text"
              placeholder="Departamento suporte"
              autofocus
            />
          </label>

          <label class="sz-create-field sz-create-field-color">
            <span class="sz-field-label">
              <span class="sz-required">*</span> Cor
            </span>
            <span
              class="sz-color-swatch"
              :style="{ background: draftLabel.color }"
              :title="draftLabel.color"
            >
              <input
                v-model="draftLabel.color"
                type="color"
                class="sz-color-input"
                aria-label="Escolher cor"
              />
            </span>
          </label>

          <label class="sz-create-field">
            <span class="sz-field-label">Descrição</span>
            <input
              v-model="draftLabel.description"
              type="text"
              placeholder="Adicione uma descrição"
            />
          </label>
        </div>

        <div class="sz-create-row sz-create-row-footer">
          <label class="sz-toggle">
            <input v-model="draftLabel.show_on_sidebar" type="checkbox" />
            <span class="sz-toggle-box" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            <span class="sz-toggle-label">Exibir etiqueta na barra lateral</span>
          </label>
          <button
            type="button"
            class="sz-btn-save"
            @click="saveDraft"
            :disabled="!draftLabel.title.trim()"
          >
            Salvar etiqueta
          </button>
        </div>
      </div>
    </section>

    <section v-if="newLabels.length" class="sz-new-labels">
      <h5>Novas etiquetas (serão criadas no envio)</h5>
      <div class="sz-pill-list">
        <span
          v-for="(lbl, idx) in newLabels"
          :key="idx"
          class="sz-pill sz-pill-on"
          :style="{ '--sz-label-color': lbl.color }"
        >
          <span class="sz-pill-dot" :style="{ background: lbl.color }" />
          <span :style="{ color: lbl.color }">{{ lbl.title }}</span>
          <button type="button" class="sz-pill-remove" @click="removeNewLabel(idx)">×</button>
        </span>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
h4 { margin: 0 0 18px; font-size: 14px; font-weight: 600; }
h5 { margin: 0 0 6px; font-size: 13px; font-weight: 500; }
.sz-hint { font-size: 11px; color: #71717a; margin: 0 0 12px; }

/* ---------- Lista de pills com fade gradient ---------- */
.sz-pill-list-wrap {
  position: relative;
  /* Sem max-height por padrão — lista mostra tudo. Quando o form de criação
     abre, colapsa pra 80px e o fade gradient aparece (v-if no template). */
  overflow: visible;
  transition: max-height 0.18s ease;
}
.sz-pill-list-wrap--collapsed {
  max-height: 80px;
  overflow: hidden;
}
.sz-pill-list-fade {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 53px;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, #FFFFFF 100%);
}
.dark .sz-pill-list-fade,
body.dark .sz-pill-list-fade {
  background: linear-gradient(180deg, rgba(31,33,37,0) 0%, #1F2125 100%);
}

.sz-pill-list { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.sz-pill {
  display: inline-flex; align-items: center; gap: 8px;
  height: 28px;
  padding: 0 4px 0 10px;
  border-radius: 999px;
  border: 1px solid #f5f5f5;
  background: #ffffff;
  font-family: inherit;
  font-size: 12px; font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.sz-pill:hover { border-color: rgba(0,0,0,0.12); }

.sz-pill-dot {
  width: 9px; height: 9px; border-radius: 999px; flex-shrink: 0;
}
.sz-pill-text { line-height: 20px; }

.sz-pill-plus-svg {
  color: #a1a1a1; flex-shrink: 0; margin-left: 2px;
}
.sz-pill-check {
  width: 16px; height: 16px;
  border-radius: 999px;
  background: #236d23;
  color: #ffffff;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0; margin-left: 2px;
}

.sz-pill-create {
  background: #ecfdf5;
  border-color: #d1fae5;
  color: #16a34a;
  padding: 0 12px 0 10px;
}
.sz-pill-create:hover { background: #d1fae5; border-color: #a7f3d0; }
.sz-pill-create-plus { color: #16a34a; flex-shrink: 0; }
.sz-pill-create-text { color: #16a34a; }

.sz-pill-remove {
  border: none; background: transparent;
  cursor: pointer; color: inherit; font-size: 16px; line-height: 1; padding: 0 4px;
}

.sz-new-labels { margin-top: 16px; }

/* ---------- Form inline "Criar nova etiqueta" ---------- */
.sz-create-inline {
  margin-top: 12px;
  border-top: 1px solid #E5E5E5;
  border-bottom: 1px solid #E5E5E5;
  background: #ffffff;
}
.dark .sz-create-inline,
body.dark .sz-create-inline {
  background: #1F2125;
  border-color: rgba(255,255,255,0.10);
}

.sz-create-inline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid #E5E5E5;
}
.dark .sz-create-inline-header,
body.dark .sz-create-inline-header {
  border-color: rgba(255,255,255,0.10);
}
.sz-create-inline-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #000000;
}
.dark .sz-create-inline-header h5,
body.dark .sz-create-inline-header h5 { color: #fafafa; }

.sz-close {
  width: 28px; height: 28px;
  border: 0; background: transparent;
  color: #525252; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 6px; padding: 0;
}
.sz-close:hover { background: rgba(0,0,0,0.06); color: #18181b; }
.dark .sz-close,
body.dark .sz-close { color: #a1a1aa; }
.dark .sz-close:hover,
body.dark .sz-close:hover { background: rgba(255,255,255,0.06); color: #fafafa; }

.sz-create-inline-body {
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sz-create-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.sz-create-row-footer {
  justify-content: space-between;
  align-items: center;
  padding-top: 4px;
}

.sz-create-field {
  display: flex; flex-direction: column; gap: 8px;
  flex: 1;
  min-width: 0;
}
.sz-create-field-color { flex: 0 0 auto; }
.sz-field-label {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 14px; font-weight: 500; color: #000000;
  line-height: 20px;
}
.dark .sz-field-label,
body.dark .sz-field-label { color: #fafafa; }
.sz-required { color: #E7000B; }

.sz-create-field input[type="text"] {
  height: 48px;
  padding: 0 16px;
  border: 1px solid #E5E5E5;
  border-radius: 8px;
  font-size: 14px;
  background: #ffffff;
  color: #18181b;
  outline: none;
  font-family: inherit;
}
.sz-create-field input[type="text"]::placeholder { color: #525252; }
.sz-create-field input[type="text"]:focus {
  border-color: #16a34a;
  box-shadow: 0 0 0 2px rgba(22,163,74,0.15);
}
.dark .sz-create-field input[type="text"],
body.dark .sz-create-field input[type="text"] {
  background: #2a2d32; color: #fafafa;
  border-color: rgba(255,255,255,0.14);
}
.dark .sz-create-field input[type="text"]::placeholder,
body.dark .sz-create-field input[type="text"]::placeholder { color: #a1a1aa; }

/* ---------- Color picker (input nativo estilizado como swatch 48x48) ---------- */
.sz-color-swatch {
  position: relative;
  width: 48px; height: 48px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-block;
  border: 1px solid rgba(0,0,0,0.06);
  box-sizing: border-box;
  transition: transform 0.12s ease;
}
.sz-color-swatch:hover { transform: scale(1.04); }
.sz-color-input {
  position: absolute;
  inset: 0;
  width: 100%; height: 100%;
  opacity: 0;
  cursor: pointer;
  border: 0;
  padding: 0;
  background: transparent;
}

/* ---------- Checkbox custom ---------- */
.sz-toggle {
  display: inline-flex; align-items: center; gap: 8px;
  cursor: pointer;
  font-size: 14px; color: #525252;
  user-select: none;
}
.dark .sz-toggle, body.dark .sz-toggle { color: #d4d4d8; }
.sz-toggle input[type="checkbox"] { display: none; }
.sz-toggle-box {
  width: 24px; height: 24px;
  border-radius: 4px;
  border: 1px solid #d4d4d8;
  background: #ffffff;
  display: inline-flex; align-items: center; justify-content: center;
  color: transparent;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.sz-toggle input[type="checkbox"]:checked + .sz-toggle-box {
  background: #388E3C;
  border-color: #388E3C;
  color: #ffffff;
}
.dark .sz-toggle-box,
body.dark .sz-toggle-box {
  background: #2a2d32;
  border-color: rgba(255,255,255,0.20);
}

/* ---------- Botão Salvar ---------- */
.sz-btn-save {
  height: 32px;
  padding: 0 16px;
  border: 1.5px solid #388E3C;
  border-radius: 8px;
  background: #ffffff;
  color: #388E3C;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, color 0.15s, opacity 0.15s;
}
.sz-btn-save:hover:not(:disabled) {
  background: #388E3C; color: #ffffff;
}
.sz-btn-save:disabled { opacity: 0.5; cursor: not-allowed; }
.dark .sz-btn-save,
body.dark .sz-btn-save {
  background: transparent;
}
</style>
